import { readonly, ref } from 'vue'

/**
 * New-deploy detector for the SPA (drives `AppUpdateToast`).
 *
 * Nuxt stamps every build with a unique id and serves it at
 * `/_nuxt/builds/latest.json` (regenerated by each deploy, served no-cache —
 * see the routeRules in nuxt.config). This composable polls that beacon and
 * compares it with the build id baked into the RUNNING app: a mismatch means a
 * new version was deployed → `updateAvailable` flips and the toast offers a
 * refresh. A plain reload is enough to get the new build (the entry HTML is
 * no-cache too), so no service worker / cache clearing is involved.
 *
 * Checks run every POLL_MS **and** whenever the window/tab regains visibility
 * (throttled) — the visibility check is what catches the installed web-app
 * window that sits open for days and is only ever re-focused, never reloaded.
 *
 * Module-level singleton: `start()` is idempotent (called from the toast on
 * mount); state is shared app-wide. No-op in dev (no builds manifest there).
 */

const POLL_MS = 5 * 60 * 1000 // periodic check
const VISIBILITY_THROTTLE_MS = 30 * 1000 // min gap between focus-driven checks

const updateAvailable = ref(false)

// Toast dismissal ("Later"): hides it until the next focus/visibility change,
// which re-surfaces the prompt (the update is still pending, after all).
const dismissed = ref(false)

let started = false
let timer = null
let lastCheckAt = 0

// Resolved ONCE in start() (inside a guaranteed Nuxt context) so the timer /
// visibility callbacks never need useRuntimeConfig() themselves.
let currentBuildId = null
let beaconUrl = null

async function checkNow() {
  lastCheckAt = Date.now()
  try {
    // `no-store` bypasses the browser HTTP cache — always ask the server.
    const res = await fetch(beaconUrl, { cache: 'no-store' })
    if (!res.ok) return

    const latest = await res.json()

    if (latest?.id && currentBuildId && latest.id !== currentBuildId) {
      updateAvailable.value = true
      stopPolling() // it can't get "more" outdated — stop asking
    }
  }
  catch {
    // Network hiccup / mid-deploy gap — silently retry on the next tick.
  }
}

function stopPolling() {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

function onVisibilityChange() {
  if (document.visibilityState !== 'visible') return

  // Re-surface a dismissed toast — the pending update is still pending.
  if (updateAvailable.value) {
    dismissed.value = false

    return
  }

  if (Date.now() - lastCheckAt >= VISIBILITY_THROTTLE_MS) checkNow()
}

export function useAppVersionCheck() {
  /** Begin watching for new deploys (idempotent; client-only by nature). */
  function start() {
    if (started || import.meta.dev || typeof window === 'undefined') return
    started = true

    // Snapshot the running build's id + the beacon URL (honoring baseURL / CDN
    // / custom assets dir) here, where the Nuxt context is guaranteed.
    const app = useRuntimeConfig().app
    const base = app.cdnURL || app.baseURL || '/'

    currentBuildId = app.buildId ?? null
    beaconUrl = `${base}${app.buildAssetsDir || '/_nuxt/'}builds/latest.json`.replace(/\/{2,}/g, '/')

    timer = setInterval(checkNow, POLL_MS)
    document.addEventListener('visibilitychange', onVisibilityChange)
  }

  /** Hide the toast until the window is next re-focused. */
  function dismiss() {
    dismissed.value = true
  }

  /** Full reload → boots the freshly deployed build. */
  function refreshNow() {
    reloadNuxtApp({ force: true, persistState: false })
  }

  return {
    updateAvailable: readonly(updateAvailable),
    dismissed: readonly(dismissed),
    start,
    dismiss,
    refreshNow,
  }
}
